import React from 'react';
import PropTypes from 'prop-types';
import { Popup, Icon, Ref } from 'semantic-ui-react';
import moment from 'moment';
import { Select } from 'semantic-ui-react';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';

import { RelativePortal } from 'Common/Components';

import { DATE_FORMAT_FOR_UI, ReactTablePropNameEnum } from './Constants';

export const FILTER_VALUES = {
  ALL: '',
};

export const caseInsensitiveFilter = (filter, row) => {
  return (
    String(row[filter.id])
      .toLowerCase()
      .indexOf(String(filter.value).toLowerCase()) > -1
  );
};

export const caseInsensitiveStartsWithFilter = (filter, row) => {
  return String(row[filter.id])
    .toLowerCase()
    .startsWith(String(filter.value).toLowerCase());
};

export const formatSorted = (sortOptions) => {
  return sortOptions.map((so) => ({
    SortName: so.id,
    SortDirection: so.desc ? 'desc' : 'asc',
  }));
};

export const formatFiltered = (filterOptions, filterType) => {
  return filterOptions.map((fo) => ({
    FilterName: fo.id,
    FilterValue: fo.value,
    FilterType: filterType,
  }));
};

export const dateSortMethod = (a, b) => {
  // We need to handle null/empty dates
  if (!a && !b) {
    return 0;
  } else if (a && !b) {
    return 1;
  } else if (!a && b) {
    return -1;
  } else {
    const ma = moment(a, 'MM/DD/YYYY');
    const mb = moment(b, 'MM/DD/YYYY');

    if (ma.isSame(mb)) {
      return 0;
    }

    return ma.isAfter(mb) ? 1 : -1;
  }
};

export const numericSortMethod = (a, b) => {
  if (a === b) {
    return 0;
  }
  return parseFloat(a) > parseFloat(b) ? 1 : -1;
};

export const defaultSortMethod = (a, b) => {
  if (a > b) {
    return 1;
  }

  if (a < b) {
    return -1;
  }

  return 0;
};

export const LegendMenu = (type) => {
  return type === 'Deleted' || 'SecondReview' ? undefined : (
    <Popup
      basic
      trigger={<Icon size="large" name="question circle outline" />}
      content={
        <div>
          Red row indicates that student is{' '}
          <span className="text-red">withdrawn</span>. <br />
          {type === 'Active' ? (
            <span>
              Blue Site ID indicates a{' '}
              <span className="text-blue">CEP/Provision2</span> site.
            </span>
          ) : undefined}
        </div>
      }
    />
  );
};

export const PopupTextCell = ({ text }) => (
  <Popup
    // Handle case of using this in modals
    style={{ zIndex: 10000 }}
    trigger={<span>{text}</span>}
    content={text}
    on="hover"
  />
);

export const selectedRowStyle = {
  color: '#df5a17',
  fontWeight: 'bold',
};

export const SelectFilterMethod = (filter, row) => {
  if (filter.value === FILTER_VALUES.ALL || _.isNil(filter.value)) {
    return true;
  }
  return String(filter.value) === String(row[filter.id]);
};

export const OnOrAfterDateFilterMethod = (filter, row) => {
  if (_.isNil(filter.value)) {
    return true;
  }

  const startDate = moment(filter.value);
  const rowStartDate = moment(row[filter.id]);

  return rowStartDate.isSameOrAfter(startDate);
};

export const OnOrBeforeDateFilterMethod = (filter, row) => {
  if (_.isNil(filter.value)) {
    return true;
  }
  const endDate = moment(filter.value);
  const rowEndDate = moment(row[filter.id]);

  return rowEndDate.isSameOrBefore(endDate);
};

export const TrueFalseOptions = [
  { text: '', value: '' },
  { text: 'Checked', value: true },
  { text: 'Unchecked', value: false },
];

export const TrueFalseFilterMethod = (filter, row) => {
  if (filter.value === FILTER_VALUES.ALL) {
    return true;
  }

  if (_.isNil(filter.value)) {
    return _.isNil(row[filter.id]);
  }

  return String(filter.value) === String(row[filter.id]);
};

export class SelectFilter extends React.Component {
  static propTypes = {
    fluid: PropTypes.bool,
    onOutClick: PropTypes.func,
  };

  static defaultProps = {
    fluid: true,
    onOutClick: () => {},
  };

  state = { open: false };

  onOpen = () => {
    this.setState({ open: true });
  };
  onClose = () => {
    this.setState({ open: false });
  };

  render() {
    const {
      filter,
      onChange,
      options,
      usePortal,
      fluid,
      onOutClick,
    } = this.props;
    const { open } = this.state;

    const SelectComponent = (
      <Select
        open={open}
        onClose={this.onClose}
        onOpen={this.onOpen}
        placeholder="All"
        options={options}
        onChange={(e, { value }) => onChange(value)}
        fluid={fluid}
        value={_.get(filter, 'value', FILTER_VALUES.ALL)}
      />
    );

    return (
      <div style={{ maxHeight: 28, width: '100%' }}>
        {usePortal && open ? (
          <RelativePortal fluid={fluid} onOutClick={onOutClick}>
            {SelectComponent}
          </RelativePortal>
        ) : (
          SelectComponent
        )}
      </div>
    );
  }
}

export class SelectHeader extends React.Component {
  static propTypes = {
    fluid: PropTypes.bool,
    onOutClick: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array,
  };

  static defaultProps = {
    fluid: true,
    onOutClick: () => {},
    options: [],
    usePortal: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      open: false,
    };
    this.selectRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { usePortal } = this.props;
    const { open: prevOpen } = prevState;
    const { open } = this.state;

    // When using portal, the whole Select component gets remounted
    // so it does not preserve its focus state so need to manually
    // re-focus it
    if (usePortal && prevOpen !== open && open) {
      this.focusInput();
    }
  }

  onOpen = () => {
    this.setState({ open: true });
  };

  onClose = () => {
    this.setState({ open: false });
  };

  focusInput = () => {
    const { current } = this.selectRef;

    if (current) {
      const input = current.childNodes[0];
      input.focus();
    }
  };

  render() {
    const {
      options,
      usePortal,
      fluid,
      onOutClick,
      value,
      onChange,
    } = this.props;
    const { open } = this.state;

    const SelectComponent = (
      <Popup
        trigger={
          <Ref innerRef={this.selectRef}>
            <Select
              open={open}
              onClose={this.onClose}
              onOpen={this.onOpen}
              options={options}
              fluid={fluid}
              onChange={onChange}
              value={value}
              search
            />
          </Ref>
        }
        disabled={!value}
        content={value}
        on="hover"
        wide
        inverted
        className="pop-up--container"
      />
    );

    return usePortal && open ? (
      <RelativePortal fluid={fluid} onOutClick={onOutClick}>
        {SelectComponent}
      </RelativePortal>
    ) : (
      SelectComponent
    );
  }
}

export const qtyOnHandSortMethod = (first, second) => {
  const firstValue = first.trim().split(' ');
  const secondValue = second.trim().split(' ');
  return Number(firstValue[0]) > Number(secondValue[0])
    ? 1
    : Number(firstValue[0]) === Number(secondValue[0])
    ? Number(firstValue[2]) > Number(secondValue[2])
      ? 1
      : -1
    : -1;
};

export const getFilteredData = (gridFilterValues, itemDetails) => {
  return Object.keys(gridFilterValues).length > 0
    ? _.filter(itemDetails, gridFilterValues)
    : itemDetails;
};

export const KitchenItemPrepareInHouseFilterEnum = {
  Yes: 'Yes',
  No: 'No',
};

export const DateCell = ({ value, dateFormat = DATE_FORMAT_FOR_UI }) => {
  return <PopupTextCell text={value ? moment(value).format(dateFormat) : ''} />;
};

export const DateColumnFilter = (filter, row, column) => {
  const id = filter.pivotId || filter.id;
  const value = row[id];

  if (!value) {
    return true;
  }

  const date = moment(value).format(DATE_FORMAT_FOR_UI);
  return date.startsWith(filter.value);
};

export const DatePickerFilter = ({ fluid, onChange, usePortal }) => {
  const [date, setSelectedDate] = React.useState(null);
  const className = classNames(
    'ui input',
    fluid && 'fluid date-picker-fluid',
    usePortal && 'rt-datepicker-portal-input'
  );

  const momentDate = moment(date, DATE_FORMAT_FOR_UI);
  const selectedDate = momentDate.isValid() ? momentDate.toDate() : null;

  const onDateChangeRaw = (e) => {
    setSelectedDate(e.target.value);
    onChange(e.target.value);
  };

  const onDateChange = (value) => {
    setSelectedDate(value);
    onChange(value);
  };

  const datePicker = (
    <DatePicker
      wrapperClassName={classNames(fluid && 'fluid')}
      className={className}
      selected={selectedDate}
      onChangeRaw={onDateChangeRaw}
      onChange={onDateChange}
    />
  );

  return usePortal ? (
    <div className="max-width">
      <RelativePortal fluid={fluid}>{datePicker}</RelativePortal>
    </div>
  ) : (
    datePicker
  );
};

/**
 * Given a dataset and a pagingModel, return a "page" with the desired data
 * @param {object[]} data - The dataset to paginate
 * @param {object} pagingModel - The paging model to apply to our dataset, can
 *   be passed from ReactTable.onFetchData or a ReactTable table state
 * @param {object} [sortMethods] - Optional mapping of column accessors to sort
 *   methods
 * @returns {object[]} - The desired page of data
 */
export const getPage = (data, pagingModel, sortMethods = {}) => {
  const {
    [ReactTablePropNameEnum.filtered]: filtered,
    [ReactTablePropNameEnum.sorted]: sorted,
    [ReactTablePropNameEnum.page]: page,
    [ReactTablePropNameEnum.pageSize]: pageSize,
  } = pagingModel;

  //call .filter regardless of whether we passed in any filters because it will
  //create a copy of our data, which is needed because sort changes its array
  let result = data.filter((row) => {
    for (let i = 0; i < filtered.length; i++) {
      if (!caseInsensitiveFilter(filtered[i], row)) {
        return false;
      }
    }

    return true;
  });

  //only sort if any sort fields were passed, otherwise use the original order
  if (sorted.length) {
    result.sort((rowA, rowB) => {
      for (let i = 0; i < sorted.length; i++) {
        const { id, desc } = sorted[i];
        const valA = rowA[id];
        const valB = rowB[id];

        let rank = 0;

        //use given sort method if possible
        const sortMethod = sortMethods[id];

        if (sortMethod) {
          rank = sortMethod(valA, valB);
        } else {
          rank = defaultSortMethod(valA, valB);
        }

        if (rank) {
          return rank * (desc ? -1 : 1);
        }
      }

      return 0;
    });
  }

  //apply paging parameters
  result = result.slice(page * pageSize, (page + 1) * pageSize);

  return result;
};

export const RecipeIdSort = (rowA, rowB, desc) => {
  const recipeIdA = parseInt(rowA.substring(2, rowA.length), 10);
  const recipeIdB = parseInt(rowB.substring(2, rowB.length), 10);

  if (recipeIdA > recipeIdB) {
    return 1;
  } else if (recipeIdB > recipeIdA) {
    return -1;
  }

  return 0;
};



// WEBPACK FOOTER //
// ./src/common/GridHelper.js